// Worth a look's lists (SCENARIOS E1, PLAN-2026-10-27-worth-a-look.md §3),
// worked out from what a host has already read: the pool's rows of the
// players on no team, each one's points by week, the league's weeks and
// games, and every player's place on his club's depth chart.
//
// ONE COPY FOR EVERY HOST: the league store answers a real league with it and
// the simulator the practice league, so the two cannot list by different
// rules. Pure, and no sport is named: a status is ruled out by the severity
// the sport gives it, and a chart's column is its own word.

import {
  WORTH_NEXT_UP_MAX,
  WORTH_PER_POSITION,
  type WorthALookView,
  type WorthItem,
  type WorthWeekPoints,
} from '../api/worth';
import type { PlayerRowView, PlayerSummary } from '../api/views';

type Severity = 'none' | 'warn' | 'likely-out' | 'out';

export interface WorthInput {
  /** The pool's rows of every player on no team in the league. */
  rows: readonly PlayerRowView[];
  /** The league's last weeks that have ended, oldest first, each with the host's key for it. */
  weeks: readonly { period: number; label: string; key: string }[];
  /** His points in a week, in the league's scoring; undefined with no line. */
  pointsIn(playerId: string, week: string): number | undefined;
  /** Whether a club had a game in a week. */
  played(club: string, period: number): boolean;
  /** The positions the league starts, in the sport's order, each as it is named. */
  positions: readonly { id: string; label: string; name: string }[];
  /** Every player of the league's world, for the depth charts. */
  world: Iterable<{
    id: string;
    proTeam: string | null;
    depth: { position: string; rank: number } | null;
    injuryStatus: string | null;
  }>;
  /** How serious a status is, as the sport says; null for one it does not know. */
  severity(status: string): Severity | null;
  /** A player as a row names him. */
  summary(playerId: string): PlayerSummary;
  /** His points a game this season, null before his first. */
  seasonAvg(playerId: string): number | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Each of the weeks for one player, and his points a game over the ones he played. */
export function latelyOf(
  input: Pick<WorthInput, 'weeks' | 'pointsIn' | 'played'>,
  row: PlayerRowView,
): { weeks: WorthWeekPoints[]; lately: number | null; total: number } {
  const club = row.player.proTeam;
  const weeks = input.weeks.map((w): WorthWeekPoints => {
    const points = input.pointsIn(row.player.id, w.key);
    if (points !== undefined) return { period: w.period, points, kind: 'played' };
    // A BYE NEEDS A CLUB: a player with none did not play, and was on no bye.
    const bye = club !== null && !input.played(club, w.period);
    return { period: w.period, points: null, kind: bye ? 'bye' : 'none' };
  });
  const played = weeks.filter((w) => w.points !== null).map((w) => w.points!);
  const total = round2(played.reduce((s, p) => s + p, 0));
  return { weeks, lately: played.length === 0 ? null : round2(total / played.length), total };
}

/** The view: each position's three with the most lately, and the next men up. */
export function worthLists(input: WorthInput): WorthALookView {
  const ruledOut = (status: string | null, also: readonly Severity[] = ['out']) => {
    if (status === null) return false;
    const s = input.severity(status);
    return s !== null && also.includes(s);
  };

  // LATELY (§3.1): at least one line in the weeks, more than nothing a game,
  // and not ruled out this week; at his first position, the matchup cue's
  // rule, so nobody is listed twice.
  const scored = input.rows
    .map((row) => ({ row, ...latelyOf(input, row) }))
    .filter(
      (s) =>
        s.lately !== null &&
        s.lately > 0 &&
        !ruledOut(s.row.player.injury === null ? null : s.row.player.injury.status),
    )
    .sort(
      (a, b) =>
        b.lately! - a.lately! ||
        b.total - a.total ||
        a.row.player.name.localeCompare(b.row.player.name) ||
        (a.row.player.id < b.row.player.id ? -1 : 1),
    );
  const positions = input.positions.map((p) => ({
    id: p.id,
    label: p.label,
    name: p.name,
    items: scored
      .filter((s) => s.row.player.positions[0] === p.id)
      .slice(0, WORTH_PER_POSITION)
      .map((s, i): WorthItem => ({
        row: s.row,
        weeks: s.weeks,
        lately: s.lately,
        because: { kind: 'lately', rank: i + 1 },
      })),
  }));

  // NEXT MAN UP (§3.2): the first string at his place on his club's chart
  // is out or doubtful, and he is the first below that player who is not.
  const charts = new Map<string, { id: string; rank: number; status: string | null }[]>();
  for (const p of input.world) {
    if (p.proTeam === null || p.depth === null) continue;
    const key = `${p.proTeam}|${p.depth.position}`;
    const chart = charts.get(key) ?? [];
    chart.push({ id: p.id, rank: p.depth.rank, status: p.injuryStatus });
    charts.set(key, chart);
  }
  for (const chart of charts.values())
    chart.sort((a, b) => a.rank - b.rank || (a.id < b.id ? -1 : 1));
  const doubtful: readonly Severity[] = ['out', 'likely-out'];
  const order = new Map(input.positions.map((p, i) => [p.id, i]));
  const nextUp: { item: WorthItem; aheadAvg: number | null }[] = [];
  for (const row of input.rows) {
    const depth = row.player.depth ?? null;
    const club = row.player.proTeam;
    if (depth === null || club === null) continue;
    const chart = charts.get(`${club}|${depth.position}`) ?? [];
    // THE FIRST STRING IS EVERYBODY AT THE CHART'S TOP RANK: a chart that
    // lists two there has two starters, and neither steps in for the other.
    const top = chart[0]?.rank;
    const firsts = chart.filter((p) => p.rank === top);
    const first = firsts.find((p) => ruledOut(p.status, doubtful));
    if (first === undefined || firsts.some((p) => p.id === row.player.id)) continue;
    const next = chart.find((p) => p.rank !== top && !ruledOut(p.status, doubtful));
    if (next?.id !== row.player.id) continue;
    const aheadAvg = input.seasonAvg(first.id);
    const { weeks, lately } = latelyOf(input, row);
    nextUp.push({
      item: {
        row,
        weeks,
        lately,
        because: {
          kind: 'next-up',
          column: depth.position,
          ahead: input.summary(first.id),
          aheadAvg,
        },
      },
      aheadAvg,
    });
  }
  // THE BIGGEST ROLE FIRST: the points a game of the player he steps in for,
  // a player ahead with none after every one with some; then the league's
  // order of positions, then by name.
  nextUp.sort(
    (a, b) =>
      (b.aheadAvg ?? -Infinity) - (a.aheadAvg ?? -Infinity) ||
      (order.get(a.item.row.player.positions[0] ?? '') ?? 99) -
        (order.get(b.item.row.player.positions[0] ?? '') ?? 99) ||
      a.item.row.player.name.localeCompare(b.item.row.player.name) ||
      (a.item.row.player.id < b.item.row.player.id ? -1 : 1),
  );

  return {
    weeks: input.weeks.map((w) => ({ period: w.period, label: w.label })),
    positions,
    nextUp: nextUp.slice(0, WORTH_NEXT_UP_MAX).map((n) => n.item),
    off: false,
  };
}
